import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@store/store';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as gridgioModalActions } from '@store/slice/gridgio/modal-slice';
import { actions as newsletterActions } from '@store/slice/newsletter-slice';
import { actions as cacheActions } from '@store/slice/cache-slice';
import { actions as hubActions } from '@store/slice/hub-slice';
import { actions as studentActions } from '@store/slice/student-slice';
import { actions as testActions } from '@store/slice/test-slice';
import { actions as errorActions } from '@store/slice/error-slice';
import { actions as authActions } from '@store/slice/auth-slice';
import { actions as userActions } from '@store/slice/user-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { actions as adminActions } from '@store/slice/admin-slice';
import { actions as vocabularyBookActions } from '@store/slice/vocabulary-book-slice';
import { actions as speakingActions } from '@store/slice/speaking-slice';
import {
  createClassroomPath,
  createSpeakingPath,
  createClipPath,
  createClozePath,
  createHomePath,
  createHubPath,
  createStudioPath,
  createTestPath,
  createVocabularyBookPath,
  IYAGIGO_PATH,
} from '@constant/iyagigo/route-path';

export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useStoreInitialize = () => {
  const dispatch = useTypedDispatch();
  const currentMainMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.mainMenu);
  const currentSubMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu);

  // store 전체 초기화 후, navigation 메뉴 상태를 복원하고 복원된 메뉴의 홈 경로를 반환
  const initializeStore = (): string => {
    const savedMainMenu = currentMainMenu;
    const savedSubMenu = currentSubMenu;

    // 전체 초기화
    dispatch(cacheActions.initialize());
    dispatch(hubActions.initialize());
    dispatch(studentActions.initialize());
    dispatch(testActions.initialize());
    dispatch(errorActions.initialize());
    dispatch(newsletterActions.initialize());
    dispatch(navigationActions.initialize());
    dispatch(authActions.initialize());
    dispatch(userActions.initialize());
    dispatch(workbookActions.initialize());
    dispatch(modalActions.initialize());
    dispatch(gridgioModalActions.initialize());
    dispatch(classroomActions.initialize());
    dispatch(adminActions.initialize());
    dispatch(vocabularyBookActions.initialize());
    dispatch(speakingActions.initialize());

    // navigation 메뉴 상태 복원
    if (savedMainMenu && savedSubMenu) {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: savedMainMenu, subMenu: savedSubMenu }));
    }

    // 복원된 메뉴의 경로 반환 (subMenu 있으면 sub 경로, 없으면 home으로 폴백)
    switch (savedMainMenu) {
      case 'home':
        return createHomePath();
      case 'cloze':
        return createClozePath('home');
      case 'classroom':
        return createClassroomPath('sub');
      case 'clip':
        return createClipPath('sub');
      case 'speaking':
        return createSpeakingPath();
      case 'test':
        return createTestPath('home');
      case 'studio':
        return createStudioPath('sub');
      case 'hub':
        return createHubPath('sub');
      case 'vocabulary-book':
        return createVocabularyBookPath('home');
      default:
        return IYAGIGO_PATH.INITIAL_SERVICE_PATH;
    }
  };

  return { initializeStore };
};
